import { CSSProperties, useEffect, useRef, useState } from 'react';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import gsap from 'gsap';
import { useAppSelector } from 'hooks';
import { Promotion } from 'reducers/playerPromos';

import HorizontalScrollContainer from 'components/HorizontalScrollContainer';

import PromoDetailsModal from './PromoDetailsModal';
import { buildPromoDisplay } from './promoDisplay';

const PlayerPromos = () => {
  const { promotions } = useAppSelector((state) => state.playerPromos);
  const [selectedPromo, setSelectedPromo] = useState<Promotion | null>(null);
  const activePromos = promotions.filter((p: any) => p.is_active);

  if (activePromos.length === 0) return null;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <EmojiEventsIcon style={{ fontSize: '1rem', color: '#E98C26' }} />
        <span style={labelStyle}>Promotions</span>
      </div>
      <HorizontalScrollContainer>
        {activePromos.map((promo) => (
          <PromoCard
            key={`promo-${promo.promo_id}`}
            promo={promo}
            onOpen={() => setSelectedPromo(promo)}
          />
        ))}
      </HorizontalScrollContainer>
      {selectedPromo && (
        <PromoDetailsModal promo={selectedPromo} onClose={() => setSelectedPromo(null)} />
      )}
    </div>
  );
};

const PromoCard = ({ promo, onOpen }: { promo: Promotion; onOpen: () => void }) => {
  const cardRef = useRef<HTMLDivElement>(null);
  const fillRef = useRef<HTMLDivElement>(null);
  const display = buildPromoDisplay(promo);

  useEffect(() => {
    if (cardRef.current) {
      gsap.fromTo(
        cardRef.current,
        { opacity: 0, y: 10 },
        { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' }
      );
    }

    if (fillRef.current) {
      gsap.fromTo(
        fillRef.current,
        { width: '0%' },
        { width: `${display.progressPct}%`, duration: 1, delay: 0.3, ease: 'power2.out' }
      );
    }
  }, [display.progressPct]);

  return (
    <div ref={cardRef} style={cardStyle} onClick={onOpen}>
      <div style={cardHeaderStyle}>
        <div>
          <div style={promoNameStyle}>{promo.promo_name}</div>
          <div style={formatStyle}>{display.formatLabel}</div>
        </div>
        <div style={statusPillStyle}>{display.statusLabel}</div>
      </div>

      <div style={metricRowStyle}>
        <span>{display.metricLabel}</span>
        <strong>{display.progressLabel}</strong>
      </div>

      <div style={tierBarContainerStyle}>
        <div style={{ ...hoursBadgeStyle, left: `${display.progressPct}%` }}>
          <span style={hoursBadgeText}>{display.earnedLabel}</span>
        </div>

        <div style={tierTrackStyle}>
          <div ref={fillRef} style={tierFillStyle} />
        </div>

        {display.tiers.map((tier) => {
          const pct = display.targetValue > 0 ? (tier.threshold / display.targetValue) * 100 : 0;
          return (
            <div
              key={`${tier.threshold}-${tier.valueLabel}`}
              style={{ ...tierMarkerStyle, left: `${Math.min(pct, 100)}%` }}
            >
              <div
                style={{
                  ...tierIconStyle,
                  backgroundColor: tier.reached ? '#E98C26' : 'rgba(255,255,255,0.15)',
                  borderColor: tier.reached ? '#E98C26' : 'rgba(255,255,255,0.25)'
                }}
              >
                <span style={{ fontSize: '0.55rem', fontWeight: 800 }}>{tier.label}</span>
              </div>
            </div>
          );
        })}
      </div>

      <div style={detailGridStyle}>
        {display.detailRows.map((row) => (
          <div key={row.label} style={detailItemStyle}>
            <span>{row.label}</span>
            <strong>{row.value}</strong>
          </div>
        ))}
      </div>

      <div style={endDateStyle}>
        {[display.resetLabel, display.endLabel].filter(Boolean).join(' | ')}
      </div>
    </div>
  );
};

export { default as PromoDetailsModal } from './PromoDetailsModal';
export { default as PromoEntryModal } from './PromoEntryModal';
export { default as AccountPromoBanner } from './AccountPromoBanner';

export default PlayerPromos;

const containerStyle: CSSProperties = {
  marginBottom: '1rem'
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: '0.5rem',
  marginBottom: '0.5rem',
  paddingLeft: '4px'
};

const labelStyle: CSSProperties = {
  fontSize: 'clamp(0.9rem, 3vw, 1.1rem)',
  fontWeight: 700,
  color: 'rgba(255, 255, 255, 0.9)'
};

const cardStyle: CSSProperties = {
  width: 'clamp(284px, 72vw, 380px)',
  minWidth: 'clamp(284px, 72vw, 380px)',
  flexShrink: 0,
  backgroundColor: 'rgba(22, 30, 42, 0.92)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: '8px',
  padding: '12px 14px 10px',
  cursor: 'pointer',
  display: 'flex',
  flexDirection: 'column',
  gap: '10px'
};

const cardHeaderStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '10px',
  alignItems: 'flex-start'
};

const promoNameStyle: CSSProperties = {
  fontSize: '0.85rem',
  fontWeight: 700,
  color: '#ffffff',
  lineHeight: 1.2
};

const formatStyle: CSSProperties = {
  color: 'rgba(255,255,255,0.52)',
  fontSize: '0.62rem',
  marginTop: 2
};

const statusPillStyle: CSSProperties = {
  backgroundColor: 'rgba(233, 140, 38, 0.16)',
  border: '1px solid rgba(233, 140, 38, 0.45)',
  borderRadius: 6,
  color: '#f4a340',
  fontSize: '0.55rem',
  fontWeight: 800,
  lineHeight: 1.2,
  maxWidth: 116,
  padding: '3px 6px',
  textAlign: 'right'
};

const metricRowStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'space-between',
  gap: '8px',
  color: 'rgba(255,255,255,0.78)',
  fontSize: '0.65rem'
};

const tierBarContainerStyle: CSSProperties = {
  position: 'relative',
  paddingTop: '30px',
  paddingBottom: '6px'
};

const tierTrackStyle: CSSProperties = {
  width: '100%',
  height: 6,
  backgroundColor: 'rgba(255, 255, 255, 0.1)',
  borderRadius: 3,
  overflow: 'hidden',
  position: 'relative'
};

const tierFillStyle: CSSProperties = {
  height: '100%',
  borderRadius: 3,
  background: 'linear-gradient(90deg, #E98C26, #e8b84a)',
  width: '0%'
};

const hoursBadgeStyle: CSSProperties = {
  position: 'absolute',
  top: 0,
  transform: 'translateX(-50%)',
  display: 'flex',
  alignItems: 'center',
  backgroundColor: '#E98C26',
  borderRadius: '8px',
  maxWidth: '150px',
  padding: '3px 8px',
  whiteSpace: 'nowrap',
  zIndex: 2
};

const hoursBadgeText: CSSProperties = {
  color: '#ffffff',
  fontSize: '0.65rem',
  fontWeight: 800,
  lineHeight: 1,
  overflow: 'hidden',
  textOverflow: 'ellipsis'
};

const tierMarkerStyle: CSSProperties = {
  position: 'absolute',
  top: '50%',
  transform: 'translate(-50%, -50%)',
  marginTop: '16px',
  zIndex: 1
};

const tierIconStyle: CSSProperties = {
  width: 30,
  height: 24,
  borderRadius: '6px',
  border: '2px solid',
  color: '#ffffff',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  transition: 'all 0.3s ease'
};

const detailGridStyle: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
  gap: '6px'
};

const detailItemStyle: CSSProperties = {
  backgroundColor: 'rgba(255,255,255,0.05)',
  borderRadius: 6,
  color: 'rgba(255,255,255,0.58)',
  display: 'flex',
  flexDirection: 'column',
  fontSize: '0.55rem',
  gap: '2px',
  padding: '6px 8px'
};

const endDateStyle: CSSProperties = {
  fontSize: '0.6rem',
  color: 'rgba(255, 255, 255, 0.4)',
  textAlign: 'right'
};
